import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Avatar, AvatarGroup, Box, Grid, IconButton, Typography, useMediaQuery } from "@mui/material";

import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import globalStyles from "../../../../../assets/styles/global.module.scss";
import styles from "../../../../../assets/styles/componentStyles/organizationTeamsStyles/teams.module.scss";

import { useShallow } from "zustand/react/shallow";
import { UserContext, UserContextPayload } from "../../../../../context/userContext";
import { useOrganizationTeamsStore } from "../../../../../store/organizationTeamsStore";
import { globalMenuItemData, organizationTeamsData, organizationUserDataModel } from "../../../../../models/axiosModel";

import GlobalSearch from "../../../../utility/GlobalSearch/GlobalSearch";
import GlobalButton from "../../../../utility/globalButton/button";
import GlobalPopUp from "../../../../utility/globalPopUp";
import GlobalDeletePopup from "../../../../utility/globalDeletePopup/globalDeletePopup";
import GlobalMenu from "../../../../globalMenu";

import { getOrganizationUserDataForTeams } from "../utils/getOrganizationUserData";
import AddEditTeamPopup from "../utils/AddEditTeamPopup";
import { hasOrganizationTeamManageAccess } from "../../../../../utils/constants";
import { formatOrganizationTeam } from "../constant.teams";
import { useGenericStore } from "../../../../../store/genericStore";
import { getInitials } from "../../../../../pages/ruleBasedInbox/utils";

interface Props {
    getOrganizationTeamsListApiCall: () => void;
}

const Teams = (props: Props) => {

    const { getOrganizationTeamsListApiCall } = props;

    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;
    const { organizationDetails, userDetail, userAssignmentRoleData, apiRoutesPermissionAssignment } = useContext(UserContext) as UserContextPayload;

    const { organizationTeamsData, filteredOrganizationTeamsData, setFilteredOrganizationTeamsData, createNewTeamPopup, 
        setCreateNewTeamPopup, selectedTeam, setSelectedTeam, editTeamPopup, setEditTeamPopup, deleteTeamPopup, setDeleteTeamPopup,
        handleDeleteTeam } = useOrganizationTeamsStore(
        useShallow((state) => ({
            organizationTeamsData: state.organizationTeamsData,
            filteredOrganizationTeamsData: state.filteredOrganizationTeamsData,
            setFilteredOrganizationTeamsData: state.setFilteredOrganizationTeamsData,
            createNewTeamPopup: state.createNewTeamPopup,
            setCreateNewTeamPopup: state.setCreateNewTeamPopup,
            selectedTeam: state.selectedTeam,
            setSelectedTeam: state.setSelectedTeam,
            editTeamPopup: state.editTeamPopup,
            setEditTeamPopup: state.setEditTeamPopup,
            deleteTeamPopup: state.deleteTeamPopup,
            setDeleteTeamPopup: state.setDeleteTeamPopup,
            handleDeleteTeam: state.handleDeleteTeam,
        }))
    )

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const organization_id = searchParams.get('organization_id');

    const breakPoints = {
        flagSmallScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1000px)'),
        mobile: useMediaQuery<boolean>('(max-width: 600px)'),
    }

    const innerHeight = breakPoints.mobile ? (window.innerHeight - 320) : (window.innerHeight - 370);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [currentRowIndex, setCurrentRowIndex] = useState<string | null>(null);

    // Menu
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, team: organizationTeamsData) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget); 
        setCurrentRowIndex(team?.organization_team_id);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setCurrentRowIndex(null);
    };

    const menuItem: globalMenuItemData[] = [
        {
            type: "item",
            label: "Edit",
            icon: <EditOutlinedIcon sx={{ fontSize: "20px" }} />,
            onClick: (team: organizationTeamsData) => { setSelectedTeam(formatOrganizationTeam(team)); setEditTeamPopup(true); },
            isDisable: false
        },
        {
            type: "item",
            label: "Delete",
            icon: <DeleteOutlinedIcon sx={{ fontSize: "20px" }} />,
            onClick: (team: organizationTeamsData) => { setSelectedTeam(formatOrganizationTeam(team)); setDeleteTeamPopup(true); },
            isDisable: false
        }
    ];

    // Search
    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e?.target?.value?.toLowerCase();

        if (organizationTeamsData) {
            const filter_data = organizationTeamsData.filter((team: organizationTeamsData) => {
                return (team?.organization_team_name?.toLowerCase().includes(query))
            })
            setFilteredOrganizationTeamsData(filter_data);
        } else {
            setFilteredOrganizationTeamsData(null);
        }
    }

    // Delete Team
    const handleCloseDeleteTeamPopup = () => {
        setDeleteTeamPopup(false);
        setSelectedTeam(null);
    };

    const handleDeleteTeamApiCall = async () => {
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: true }));
        const res = await handleDeleteTeam();
        if(!(res.status)){
            handleError(res);
        }
        else {
            showSuccess("Team Deleted Successfully!")
        }
        setFlagLoading((prevState) => ({ ...prevState, flagRoundLoader: false }));
    }

    // Team Card
    const handleTeamCardClick = (team: organizationTeamsData) => {
        const team_id = team?.organization_team_id;
        if (!team_id || !organization_id) {
            return;
        }
        setSelectedTeam(formatOrganizationTeam(team));
        navigate(`/organization/teams?team_id=${team_id}&organization_id=${organization_id}`);
    };

    return (
        <>
            <Grid container direction={"column"} className={styles.teamsContainer}>

                {/* Search and New Team Button */}
                <Grid item>
                    <Grid container flexWrap="nowrap" alignItems="center" className={`${globalStyles.fullwidth} ${styles.teamsHeader}`}>
                        <Grid item className={styles.searchContainer}>
                            <GlobalSearch
                                fullWidth
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => { handleSearch(e) }}
                                size="small"
                                placeholder="Search teams..."
                            />
                        </Grid>

                        {hasOrganizationTeamManageAccess(apiRoutesPermissionAssignment, userDetail) && (
                            <Grid item className={styles.newTeamButtonContainer}>
                                <GlobalButton
                                    buttonText="New Team"
                                    className="primaryButtonStyle"
                                    startIcon={<AddIcon />}
                                    onClick={() => { setCreateNewTeamPopup(true) }}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>

                {/* Teams Card */}
                <Box className={styles.teamsCardContainer}
                    sx={{ maxHeight: `${innerHeight}px !important`, overflow: "auto" }}
                >
                    {filteredOrganizationTeamsData && filteredOrganizationTeamsData?.map((team: organizationTeamsData) => (
                        <Box key={team.organization_team_id} className={styles.teamCard}>
                            <Grid
                                className={`${styles.teamCardContent} ${globalStyles.clickable}`}
                                onClick={() => { handleTeamCardClick(team) }}
                            >
                                {/* Team Card Header */}
                                <Grid className={styles.teamCardContentHeader}>
                                    <Grid className={styles.teamCardContentHeaderLeft}>

                                        {/* Team Initial */}
                                        <Box className={styles.teamCardContentHeaderInitial}>
                                            {getInitials(team?.organization_team_name)}
                                        </Box>

                                        {/* Team Info */}
                                        <Box className={styles.teamCardContentHeaderInfo}>
                                            <Typography className={styles.teamCardContentHeaderInfoName}>
                                                {team?.organization_team_name}
                                            </Typography>

                                            <Typography
                                                className={styles.teamCardContentHeaderInfoMembers}
                                            >
                                                {team?.organization_team_users?.length ?? 0} members
                                             </Typography>
                                        </Box>
                                    </Grid>

                                    {/* More Vert Icon */}
                                    {hasOrganizationTeamManageAccess(apiRoutesPermissionAssignment, userDetail) && (
                                        <>
                                            <IconButton
                                                size="small"
                                                aria-label="team actions"
                                                className={styles.teamCardMoreButton}
                                                onClick={(event: React.MouseEvent<HTMLElement>) => {
                                                    event.stopPropagation();
                                                    handleMenuOpen(event, team);
                                                }}
                                            >
                                                <MoreVertIcon className={styles.teamCardContentHeaderMoreVert} />
                                            </IconButton>
                                            
                                            {/* Menu */}
                                            <GlobalMenu
                                                anchorEl={anchorEl}
                                                handleMenuClose={handleMenuClose}
                                                menuItemData={menuItem}
                                                open={Boolean(anchorEl) && currentRowIndex === team?.organization_team_id}
                                                additionalField={team}
                                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                            />
                                        </>
                                    )}
                                </Grid>

                                {/* Members Avatars */}
                                <AvatarGroup
                                    max={breakPoints.flagSmallScreen ? 5 : 10}
                                    className={styles.teamCardContentMembers}
                                >
                                    {team?.organization_team_users?.map((user) => (
                                        <Avatar
                                            key={user?.organization_team_user_id}
                                            className={styles.teamCardContentMembersAvatar}
                                        >
                                            {getInitials(user?.first_name, user?.last_name)}
                                        </Avatar>
                                    ))}
                                </AvatarGroup>
                            </Grid>
                        </Box>
                    ))}

                    {/* No Teams Found */}
                    {(organizationTeamsData !== null && !filteredOrganizationTeamsData?.length) && (
                        <Box className={styles.noTeamsEmptyState}>
                            <Typography className={styles.noTeamsText}>
                                No teams found
                            </Typography>

                            <Typography className={styles.noTeamsSubtext}>
                                { hasOrganizationTeamManageAccess(apiRoutesPermissionAssignment, userDetail) 
                                    ? "Create a team to organize your workspace members." 
                                    : "No teams are currently available in this workspace to view."
                                }
                            </Typography>

                            {hasOrganizationTeamManageAccess(apiRoutesPermissionAssignment, userDetail) &&
                                <GlobalButton
                                    buttonText="Create Team"
                                    className="primaryButtonStyle"
                                    startIcon={<AddIcon />}
                                    onClick={() => setCreateNewTeamPopup(true)}
                                />
                            }
                        </Box>
                    )}
                </Box>
            </Grid>

            {/* Edit Team Popup */}
            {editTeamPopup && (
                <GlobalPopUp
                    open={editTeamPopup}
                    onClose={() => { setEditTeamPopup(false) }}
                    handleClose={() => { setEditTeamPopup(false) }}
                    paperMinWidth={breakPoints.flagSmallScreen ? "310px" : "750px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "610px" : "750px"}
                    flagShowCloseIcon={true}
                    titlePadding="0px"
                >
                    <AddEditTeamPopup 
                        selectedTeam={selectedTeam as organizationTeamsData} 
                        getOrganizationTeamsListApiCall={getOrganizationTeamsListApiCall}
                        organizationUserData={getOrganizationUserDataForTeams({ organizationDetails, organization_id, timezoneHour: userDetail?.timezone_hour, userAssignmentRoleData }) as organizationUserDataModel[]}
                    />
                </GlobalPopUp>
            )}

            {/* Add Team Popup */}
            {createNewTeamPopup && (
                <GlobalPopUp
                    open={createNewTeamPopup}
                    onClose={() => { setCreateNewTeamPopup(false) }}
                    handleClose={() => { setCreateNewTeamPopup(false) }}
                    paperMinWidth={breakPoints.flagSmallScreen ? "310px" : "750px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "610px" : "750px"}
                    flagShowCloseIcon={true}
                    titlePadding="0px"
                >
                    <AddEditTeamPopup
                        getOrganizationTeamsListApiCall={getOrganizationTeamsListApiCall}
                        organizationUserData={getOrganizationUserDataForTeams({ organizationDetails, organization_id, timezoneHour: userDetail?.timezone_hour, userAssignmentRoleData }) as organizationUserDataModel[]}
                    />
                </GlobalPopUp>
            )}

            {/* Delete Team Popup */}
            {deleteTeamPopup && (
                <GlobalDeletePopup
                    deleteDescription={`Are you sure you want to delete ${selectedTeam ? `"${selectedTeam?.organization_team_name}"` : 'this'} team`}
                    open={deleteTeamPopup}
                    onClose={() => { handleCloseDeleteTeamPopup() }}
                    onDelete={() => { handleDeleteTeamApiCall() }}
                />
            )}
        </>
    )
}

export default Teams;